import { useEffect, useState } from "react";
import { Warning } from "@phosphor-icons/react";
import api from "../../lib/api";
import { fmt$ } from "./shared";

const pct = (v) => (v === null || v === undefined ? "—" : `${v > 0 ? "+" : ""}${Number(v).toFixed(1)}%`);

export function Costing({ refreshKey }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/scm/costing").then((r) => setData(r.data)).catch(() => {}); }, [refreshKey]);
  if (!data) return <div className="p-6 text-xs font-mono text-zinc-500">Loading costing…</div>;
  const flagged = data.products.filter((p) => p.flag).length;

  return (
    <div data-testid="costing-panel">
      <div className="px-5 py-3 border-b border-zinc-950 flex flex-wrap items-center justify-between gap-2 text-xs">
        <div className="font-mono text-zinc-500">MOVING WEIGHTED AVERAGE · every goods receipt re-averages the product cost · variance = last PO price vs avg cost</div>
        <div className={`font-mono ${flagged ? "text-[#D0021B]" : "text-emerald-700"}`} data-testid="costing-flag-count">{flagged} SKU{flagged === 1 ? "" : "s"} beyond ±{data.alert_threshold_pct}%</div>
      </div>
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs tracking-wider uppercase">
            <th className="text-left px-4 py-2.5">SKU</th>
            <th className="text-left px-4 py-2.5">Product</th>
            <th className="text-right px-4 py-2.5">Stock</th>
            <th className="text-right px-4 py-2.5">Avg Cost</th>
            <th className="text-right px-4 py-2.5">Last PO Cost</th>
            <th className="text-right px-4 py-2.5">Variance</th>
            <th className="text-right px-4 py-2.5">Sell Price</th>
            <th className="text-right px-4 py-2.5">Inventory Value</th>
          </tr>
        </thead>
        <tbody data-testid="costing-tbody">
          {data.products.map((p) => (
            <tr key={p.id} className={`border-b border-zinc-100 ${p.flag ? "bg-red-50/40" : ""}`} data-testid={`costing-row-${p.sku}`}>
              <td className="px-4 py-2.5 tnum text-xs">{p.sku}</td>
              <td className="px-4 py-2.5 font-medium">{p.name}</td>
              <td className="px-4 py-2.5 text-right tnum">{p.stock}</td>
              <td className="px-4 py-2.5 text-right tnum font-semibold" data-testid={`costing-avg-${p.sku}`}>{fmt$(p.avg_cost)}</td>
              <td className="px-4 py-2.5 text-right tnum text-zinc-600">{p.last_po_cost === null || p.last_po_cost === undefined ? "—" : fmt$(p.last_po_cost)}<div className="text-[10px] text-zinc-400">{p.last_po_no || ""}</div></td>
              <td className={`px-4 py-2.5 text-right tnum ${p.flag ? "text-[#D0021B] font-semibold" : "text-zinc-600"}`} data-testid={`costing-var-${p.sku}`}>
                <span className="inline-flex items-center gap-1">{p.flag && <Warning size={11} />}{pct(p.variance_pct)}</span>
              </td>
              <td className="px-4 py-2.5 text-right tnum text-zinc-600">{fmt$(p.unit_price)}</td>
              <td className="px-4 py-2.5 text-right tnum">{fmt$(p.inventory_value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <div className="px-5 py-3 border-t border-zinc-950 label-sm text-zinc-500">GOODS RECEIPT HISTORY</div>
      <table className="w-full text-xs">
        <thead className="bg-zinc-50 border-y border-zinc-200">
          <tr className="text-[10px] tracking-wider uppercase text-zinc-500">
            <th className="text-left px-4 py-2">GRN</th>
            <th className="text-left px-4 py-2">PO</th>
            <th className="text-left px-4 py-2">Received</th>
            <th className="text-left px-4 py-2">Lines (qty @ price → new avg)</th>
          </tr>
        </thead>
        <tbody data-testid="receipts-tbody">
          {data.receipts.length === 0 && <tr><td colSpan={4} className="px-4 py-4 text-zinc-400 font-mono">No receipts yet — receive a PO to start the cost history.</td></tr>}
          {data.receipts.map((r) => (
            <tr key={r.id} className="border-b border-zinc-100" data-testid={`receipt-row-${r.grn_no}`}>
              <td className="px-4 py-2 tnum">{r.grn_no}</td>
              <td className="px-4 py-2 tnum">{r.po_no}</td>
              <td className="px-4 py-2 tnum text-zinc-500">{(r.received_at || "").slice(0, 16).replace("T", " ")}<div className="text-zinc-400">{r.received_by}</div></td>
              <td className="px-4 py-2">
                {r.lines.map((l) => (
                  <div key={l.sku} className={`tnum ${l.flag ? "text-[#D0021B]" : ""}`}>{l.sku}: {l.quantity} @ {fmt$(l.unit_price)} → avg {fmt$(l.new_avg_cost)} ({pct(l.variance_pct)})</div>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
